import React from 'react';

import PrestaGlobals from '../common/PrestaGlobals';
import Boards from "./Boards";
import Edges from './Edges';
import Fetcher from '../common/Fetcher';
import IHardware from './interfaces/IHardware';
import IDrawerSystem from './interfaces/IDrawerSystem';
import IParamScript from './interfaces/paramScript/IParamScript';

/** Database data context */
export const DatabaseContext = React.createContext<{
    boardData: Boards | null;
    edgeData: Edges | null;
    hardwareData: IHardware[] | null;
    drawerSystemData: IDrawerSystem[] | null;
    uconScript: any | null;
    paramScripts: IParamScript[] | null;
    updateScripts: (paramScripts: IParamScript[], uconScript: any) => void;
    reloadScripts: () => void;
}>({
    boardData: null,
    edgeData: null,
    hardwareData: null,
    drawerSystemData: null,
    uconScript: null,
    paramScripts: null,
    updateScripts: () => {},
    reloadScripts: () => {}
});

/**
 * DatabaseHandler component to fetch and manage data from the database
 */
const DatabaseHandler: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [boardData, setBoardData] = React.useState<Boards | null>(null);
  const [edgeData, setEdgeData] = React.useState<Edges | null>(null);
  const [hardwareData, setHardwareData] = React.useState<IHardware[] | null>(null);
  const [drawerSystemData, setDrawerSystemData] = React.useState<IDrawerSystem[] | null>(null);
  const [uconScript, setUconScript] = React.useState<any | null>(null);
  const [paramScripts, setParamScripts] = React.useState<IParamScript[] | null>(null);
  // Set by updateScripts: a late initial fetch must not overwrite loaded cart scripts
  const scriptsOverridden = React.useRef(false);

  const dataContextValue = React.useMemo(() => ({
    boardData,
    edgeData,
    hardwareData,
    drawerSystemData,
    uconScript,
    paramScripts,
    updateScripts: (newParamScripts: IParamScript[], newUconScript: any) => {
      scriptsOverridden.current = true;
      setParamScripts(newParamScripts);
      setUconScript(newUconScript);
    },
    reloadScripts: () => {
      const uconScriptFetcher: Fetcher = new Fetcher(PrestaGlobals.virtualUri + "/scripts?id_product=" + PrestaGlobals.productId);
      const paramScriptFetcher: Fetcher<IParamScript[]> = new Fetcher<IParamScript[]>(PrestaGlobals.virtualUri + "/params?id_product=" + PrestaGlobals.productId
            + "&id_shop=" + PrestaGlobals.shopId
            + "&id_cart=" + PrestaGlobals.cartId
            + "&id_customer=" + PrestaGlobals.customerId);

      console.log('Reloading scripts from the database...');
      Promise.all([
          uconScriptFetcher.request(),
          paramScriptFetcher.request()
      ])
      .then(() => {
          setUconScript(uconScriptFetcher.Result);
          setParamScripts(paramScriptFetcher.Result);
      })
      .catch((error) => {
          console.error('Error loading scripts:', error);
      });
    }
  }), [boardData, edgeData, hardwareData, drawerSystemData, uconScript, paramScripts]);

  // Fetch data on component mount
  React.useEffect(() => {
    const boards: Boards = new Boards("/boards?id_shop=" + PrestaGlobals.shopId);
    const edges: Edges = new Edges("/edges?id_shop=" + PrestaGlobals.shopId);
    const hardwareFetcher: Fetcher<IHardware[]> = new Fetcher<IHardware[]>(PrestaGlobals.virtualUri + "/hardware");
    const drawerSystemFetcher: Fetcher<IDrawerSystem[]> = new Fetcher<IDrawerSystem[]>(PrestaGlobals.virtualUri + "/drawer_systems");
    const uconScriptFetcher: Fetcher = new Fetcher(PrestaGlobals.virtualUri + "/scripts?id_product=" + PrestaGlobals.productId);
    const paramScriptFetcher: Fetcher<IParamScript[]> = new Fetcher<IParamScript[]>(PrestaGlobals.virtualUri + "/params?id_product=" + PrestaGlobals.productId
          + "&id_shop=" + PrestaGlobals.shopId
          + "&id_cart=" + PrestaGlobals.cartId
          + "&id_customer=" + PrestaGlobals.customerId);

    console.log('Loading data from the database...');
    Promise.all([
        boards.request(),
        edges.request(),
        hardwareFetcher.request(),
        drawerSystemFetcher.request(),
        uconScriptFetcher.request(),
        paramScriptFetcher.request()
    ])
        .then(() => {
            setBoardData(boards);
            setEdgeData(edges);
            setHardwareData(hardwareFetcher.Result);
            setDrawerSystemData(drawerSystemFetcher.Result);
            if (scriptsOverridden.current) return;
            setUconScript(uconScriptFetcher.Result);
            setParamScripts(paramScriptFetcher.Result);
        })
        .catch((error) => {
            console.error('Error loading data:', error);
        });
  }, []);

  return (
    <DatabaseContext.Provider value={dataContextValue}>
        {children}
    </DatabaseContext.Provider>
  );
}

export default DatabaseHandler;
